import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QuoteLanguage, QuoteSendChannel, QuoteStatus } from '@mt/shared';
import { api } from '@/lib/api';
import type { AsiaOrderDetail, Customer, Paged, Product, Quote, QuoteItem, QuoteListRow, ReconcileResult, ReconcileState, UserLite } from '@/lib/types';
import type { ProductDetail } from '../products/api';

/** DEC-066 A: trạng thái master của dòng (ngừng dùng / mã TMP- đã map) – chỉ đọc, không gửi lên server. */
export interface LineMasterStatus {
  inactive?: boolean;
  replacedByCode?: string | null;
}
export type QuoteItemDetail = QuoteItem & LineMasterStatus;
export interface QuoteDetail extends Quote {
  /** Khách ngừng dùng / đã gộp: chặn gửi khách tới khi đổi khách */
  customerInactive?: boolean;
  items: QuoteItemDetail[];
}

export interface QuoteFilter {
  q?: string;
  status?: QuoteStatus[];
  salesUserId?: number;
  dateFrom?: string;
  dateTo?: string;
  expired?: boolean;
  /** P07 B3 (DEC-113): Đã gửi / Đang đàm phán còn hiệu lực ≤ 2 ngày */
  expiring?: boolean;
  /** Nháp NV lập chưa lên Asia (bỏ bản chép từ phiếu Asia) */
  pendingDraft?: boolean;
  /** Không sửa từ N ngày */
  staleDays?: number;
  /** P06 B5: needs = cần duyệt mà chưa gửi duyệt / bị từ chối */
  approval?: 'needs' | 'todo' | 'pending' | 'approved' | 'rejected';
  /** Màn đối soát: phiếu Asia đã ghép báo giá ERP vẫn hiện */
  includeLinked?: boolean;
  /** all = báo giá ERP + phiếu SO1 lập trên Asia (mặc định màn danh sách) */
  source?: 'all' | 'erp' | 'asia';
  sort: string;
  dir: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

export const quoteKeys = {
  all: ['quotes'] as const,
  list: (f: QuoteFilter) => ['quotes', 'list', f] as const,
  detail: (id: number) => ['quotes', 'detail', id] as const,
};

export const useQuoteList = (f: QuoteFilter, enabled = true) =>
  useQuery({
    queryKey: quoteKeys.list(f),
    queryFn: () => api.get<Paged<QuoteListRow> & { sum: { grandTotal: number } }>('/quotes', { ...f }),
    placeholderData: keepPreviousData,
    enabled,
  });

/** Đối soát 1 bảng ERP ⇆ Asia: state todo = Cần xử lý, không truyền = tất cả */
export interface ReconcileFilter {
  q?: string;
  salesUserId?: number;
  dateFrom?: string;
  dateTo?: string;
  state?: ReconcileState | 'todo';
  sort: 'date' | 'amount' | 'diff';
  dir: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

/** KPI riêng từng nguồn của màn đối soát chia đôi ERP ↔ Asia (GET /quotes/summary). null = nguồn không có số liệu này. */
export interface QuoteSummary {
  source: 'erp' | 'asia';
  count: number;
  grandTotal: number;
  grossProfit: number | null;
  needsApproval: number | null;
  expiring: number | null;
  expired: number | null;
  byStatus: Record<string, number>;
}

export const useQuoteSummary = (f: Pick<QuoteFilter, 'q' | 'dateFrom' | 'dateTo' | 'salesUserId' | 'includeLinked'> & { source: 'erp' | 'asia' }, enabled = true) =>
  useQuery({
    queryKey: ['quotes', 'list', 'summary', f],
    queryFn: () => api.get<QuoteSummary>('/quotes/summary', { ...f }),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useQuoteReconcile = (f: ReconcileFilter, enabled = true) =>
  useQuery({
    queryKey: ['quotes', 'list', 'reconcile', f],
    queryFn: () => api.get<ReconcileResult>('/quotes/reconcile', { ...f }),
    placeholderData: keepPreviousData,
    enabled,
  });

export const useAsiaOrder = (id: number | null) =>
  useQuery({ queryKey: ['asia-sales-orders', id], queryFn: () => api.get<AsiaOrderDetail>(`/asia/sales-orders/${id}`), enabled: !!id });

export const useQuote = (id: number | null) =>
  useQuery({ queryKey: quoteKeys.detail(id ?? 0), queryFn: () => api.get<QuoteDetail>(`/quotes/${id}`), enabled: !!id });

export const useQuoteDefaults = (enabled: boolean) =>
  useQuery({
    queryKey: ['quotes', 'defaults'],
    queryFn: () =>
      api.get<{ quoteDate: string; validUntil: string; deliveryTerms: string; deliveryTime: string; paymentTerms: string; vatRate: number }>('/quotes/defaults'),
    enabled,
    staleTime: 5 * 60_000,
  });

export const useUsers = () => useQuery({ queryKey: ['users'], queryFn: () => api.get<UserLite[]>('/users'), staleTime: 5 * 60_000 });
/**
 * Chỉ NV kinh doanh (anh Tiến 15/10/2026: ô lọc nhân viên ở Bán hàng / Mua hàng không lấy kế toán, kho, tài xế…;
 * 19/10/2026: áp cho cả khu CRM).
 */
export function useSalesUsers() {
  const q = useUsers();
  return { ...q, data: q.data?.filter((u) => u.isSales) };
}

/** Ô chọn NV phụ trách: chỉ NV kinh doanh, nhưng vẫn giữ người đang được giao (có thể là người cũ không thuộc khối kinh doanh). */
export const salesOptions = (users: UserLite[] | undefined, currentId: number | null | undefined) =>
  users?.filter((u) => u.isSales || u.id === currentId);

export function useSaveQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body, idempotencyKey }: { id: number | null; body: unknown; idempotencyKey?: string }) =>
      id
        ? api.put<QuoteDetail>(`/quotes/${id}`, body)
        : api.post<QuoteDetail>('/quotes', body, idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined),
    onSuccess: (q) => {
      qc.setQueryData(quoteKeys.detail(q.id), q);
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] });
    },
  });
}

/** DEC-154: phiếu SO1 Asia đã sửa thẳng trên ERP: xử lý xung đột, ghi lại khi lỗi (sửa mới đi qua bản sửa trên ERP của phiếu Asia) */
export function useAsiaOrderActions(id: number | null) {
  const qc = useQueryClient();
  const done = (d: AsiaOrderDetail) => {
    qc.setQueryData(['asia-sales-orders', d.id], d);
    qc.invalidateQueries({ queryKey: ['quotes', 'list'] });
  };
  return {
    resolve: useMutation({
      mutationFn: (keep: 'erp' | 'asia') => api.post<AsiaOrderDetail>(`/asia/sales-orders/${id}/resolve`, { keep }),
      onSuccess: done,
    }),
    retry: useMutation({ mutationFn: () => api.post<AsiaOrderDetail>(`/asia/sales-orders/${id}/retry`, {}), onSuccess: done }),
  };
}

/** "Sửa phiếu này trên ERP": tạo (hoặc mở lại) bản sửa trên ERP duy nhất của phiếu Asia, mang đúng số phiếu Asia (anh Tiến 14/10/2026) */
export function useCreateAsiaErpDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (asiaId: number) => api.post<{ id: number; code: string; created: boolean }>(`/asia/sales-orders/${asiaId}/erp-draft`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quotes', 'list'] }),
  });
}

/** Admin duyệt ghi bản sửa trên ERP vào Asia: ghi ngay nếu Asia mở lệnh ghi, không thì chờ lượt ghi kế tiếp */
export function useApproveQuoteAsia() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ sent: boolean; reason?: string; result?: string; error?: string | null; quote: QuoteDetail }>(`/quotes/${id}/asia-approve`, {}),
    onSuccess: (r) => {
      qc.setQueryData(quoteKeys.detail(r.quote.id), r.quote);
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] });
    },
  });
}

/** DEC-154: báo giá ↔ phiếu SO1 Asia: ghi lại bản ERP hoặc giữ bản Asia */
export function useQuoteAsiaSync() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, keep }: { id: number; keep: 'erp' | 'asia' }) => api.post<QuoteDetail>(`/quotes/${id}/asia-sync`, { keep }),
    onSuccess: (q) => {
      qc.setQueryData(quoteKeys.detail(q.id), q);
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] });
    },
  });
}

export function useChangeStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: number; status: QuoteStatus; version: number; lostReason?: string; reason?: string; acceptedVersion?: number }) =>
      api.post<QuoteDetail>(`/quotes/${id}/status`, body),
    onSuccess: (q) => {
      qc.setQueryData(quoteKeys.detail(q.id), q);
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] });
    },
  });
}

/** DEC-297: người duyệt nhập / bỏ giá mua tạm (chưa VAT) cho dòng chưa có giá mua. */
export function useTempCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, itemId, ...body }: { id: number; itemId: number; version: number; costPrice: number | null }) =>
      api.put<QuoteDetail>(`/quotes/${id}/items/${itemId}/temp-cost`, body),
    onSuccess: (q) => {
      qc.setQueryData(quoteKeys.detail(q.id), q);
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] });
    },
  });
}

/** 20/10/2026: Kinh doanh nhập / bỏ giá nhập tham khảo cho dòng bán dưới giá nhập (CHƯA VAT / 1 ĐVT chính). */
export function useRefCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, itemId, ...body }: { id: number; itemId: number; version: number; price: number | null; vatIncluded?: boolean; note?: string | null }) =>
      api.put<QuoteDetail>(`/quotes/${id}/items/${itemId}/ref-cost`, body),
    onSuccess: (q) => {
      qc.setQueryData(quoteKeys.detail(q.id), q);
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] });
    },
  });
}

/** Giám đốc duyệt / từ chối bán dưới giá nhập tham khảo. */
export function useDecideRefCost() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, itemId, ...body }: { id: number; itemId: number; version: number; decision: 'approved' | 'rejected'; comment?: string | null }) =>
      api.post<QuoteDetail>(`/quotes/${id}/items/${itemId}/ref-cost/decision`, body),
    onSuccess: (q) => {
      qc.setQueryData(quoteKeys.detail(q.id), q);
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] });
    },
  });
}

/** P07 B2: 1 bản đã gửi khách (quote_versions, bất biến). */
export interface QuoteVersion {
  versionNo: number;
  statusAtSnapshot: QuoteStatus;
  grandTotal: number;
  channel: QuoteSendChannel | null;
  contactId: number | null;
  contactName: string | null;
  reason: 'send' | 'extend';
  note: string | null;
  /** P07 B6: ngôn ngữ lúc gửi bản này */
  language?: QuoteLanguage;
  createdByName: string;
  createdAt: string;
}

export const useQuoteVersions = (id: number, enabled: boolean) =>
  useQuery({ queryKey: ['quotes', 'versions', id], queryFn: () => api.get<QuoteVersion[]>(`/quotes/${id}/versions`), enabled });

/** P07 B2 (DEC-110 B, DEC-111 A): Gửi khách / Gửi lại = chụp 1 bản + ghi kênh, người nhận. */
export function useSendQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, idempotencyKey, ...body }: { id: number; idempotencyKey: string; channel: QuoteSendChannel; contactId: number | null; note?: string; language?: QuoteLanguage; version: number }) =>
      api.post<{ versionNo: number; quote: QuoteDetail }>(`/quotes/${id}/send`, body, { 'Idempotency-Key': idempotencyKey }),
    onSuccess: ({ quote }) => {
      qc.setQueryData(quoteKeys.detail(quote.id), quote);
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] });
      qc.invalidateQueries({ queryKey: ['quotes', 'versions', quote.id] });
    },
  });
}

/** P07 B3 (DEC-113 A): Gia hạn = ngày hiệu lực mới, giá giữ nguyên, chụp bản mới để gửi lại khách. */
export function useExtendQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      idempotencyKey,
      ...body
    }: { id: number; idempotencyKey: string; validUntil: string; channel: QuoteSendChannel; contactId: number | null; note?: string; language?: QuoteLanguage; version: number }) =>
      api.post<{ versionNo: number; validUntil: string; quote: QuoteDetail }>(`/quotes/${id}/extend`, body, { 'Idempotency-Key': idempotencyKey }),
    onSuccess: ({ quote }) => {
      qc.setQueryData(quoteKeys.detail(quote.id), quote);
      qc.invalidateQueries({ queryKey: ['quotes', 'list'] });
      qc.invalidateQueries({ queryKey: ['quotes', 'versions', quote.id] });
    },
  });
}

/**
 * Tìm hàng để thêm vào báo giá: chỉ hàng đang kinh doanh; bỏ mã tạm TMP- đã map sang mã Asia
 * (server từ chối thêm dòng mới với các mã này – DEC-066 A, DEC-068 A).
 */
export const searchProducts = (q: string, extra: { groupId?: number; brandId?: number; pageSize?: number; expandFamily?: boolean } = {}) =>
  api
    .get<Paged<ProductDetail>>('/products', {
      q,
      active: true,
      pageSize: extra.pageSize ?? 12,
      groupId: extra.groupId,
      brandId: extra.brandId,
      // Mã gốc (DEC-225): kèm mọi hãng cùng mã gốc với mã khớp từ khóa, xếp liền nhau
      expandFamily: extra.expandFamily || undefined,
    })
    .then((r): Paged<ProductDetail> => ({ ...r, items: r.items.filter((p) => p.isActive && !p.replacedByProductId) }));

export const getProductById = (id: number) => api.get<Product>(`/products/${id}`);

export const searchCustomers = (q: string) => api.get<Paged<Customer>>('/customers', { q, pageSize: 12 });
export const getCustomer = (id: number) => api.get<Customer>(`/customers/${id}`);

export interface SalePriceRow {
  /** null khi báo giá ngoài scope người xem (DEC-027 A) */
  quoteId: number | null;
  quoteCode: string | null;
  quoteDate: string;
  status: QuoteStatus;
  /** "Khách khác" khi customerHidden */
  customerName: string;
  customerHidden: boolean;
  salesName: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  discountPercent: number;
  netUnitPrice: number;
}
/** F9 (09/10/2026): một lần bán – hóa đơn Asia, sổ bán Asia hoặc báo giá ERP */
export interface SaleHistoryRow {
  source: 'asia_invoice' | 'asia' | 'erp_quote';
  date: string;
  /** null khi khách ngoài phạm vi xem (DEC-027 A) */
  docNo: string | null;
  quoteId: number | null;
  quoteStatus: QuoteStatus | null;
  customerName: string;
  customerHidden: boolean;
  salesName: string | null;
  quantity: number | null;
  unit: string | null;
  unitPrice: number;
  discountPercent: number;
  netUnitPrice: number;
}
export interface PriceSummary {
  count: number;
  last: number | null;
  min: number | null;
  max: number | null;
  avg: number | null;
}
export interface SaleHistory {
  unit: string;
  /** null khi chưa chọn khách */
  customer: { rows: SaleHistoryRow[]; summary: PriceSummary } | null;
  others: { rows: SaleHistoryRow[]; summary: PriceSummary };
  asiaError: string | null;
}
export interface PurchaseHistory {
  rows: { source: 'erp' | 'asia'; date: string; supplierName: string | null; refNo: string | null; quantity: number | null; price: number }[];
  summary: PriceSummary;
  asiaError: string | null;
}
export interface PurchasePriceRow {
  id: number;
  purchasedAt: string;
  unitPrice: number;
  quantity: number | null;
  supplierName: string | null;
  refNo: string | null;
}

/** F10 (P10 Nhánh A): 3 nguồn giá mua, giá chưa VAT 1 ĐVT chính */
export interface PurchaseSources {
  unit: string;
  erp: PurchasePriceRow[];
  asia: { price: number; date: string | null } | null;
  asiaError: string | null;
  suppliers: {
    supplierId: number;
    supplierCode: string;
    supplierName: string;
    priceListId: number;
    title: string;
    effectiveFrom: string;
    effectiveTo: string | null;
    priority: number;
    basePrice: number;
    vatRate: number | null;
    minQty: number | null;
  }[];
  bestSupplierPrice: number | null;
}

/** Chia kho lúc khách chốt (DEC-274): kho gợi ý từng dòng theo tồn Asia, phần thiếu */
export interface QuoteStockAllocation {
  quoteId: number;
  /** DEC-277: đề nghị mua phần thiếu (tự lập khi đơn qua Kiểm soát); chỉ có ở GET */
  purchaseRequest?: { id: number; code: string; status: string } | null;
  persisted: boolean;
  stockChecked: boolean;
  asOf: string | null;
  allocatedAt: string | null;
  so3: boolean;
  lines: {
    lineNo: number;
    sku: string;
    productName: string;
    unit: string;
    quantity: number;
    parts: { sku: string; warehouseCode: string; warehouseName: string | null; qty: number }[];
    shortQty: number;
    short: boolean;
    /** DEC-331: kho NV chọn trên dòng; differs = chia khác kho chọn (kho chọn không đủ lúc chốt) */
    chosenWarehouse?: string | null;
    differs?: boolean;
  }[];
  shortLines: number;
}
const allocKey = (id: number) => ['quotes', 'stock-allocation', id] as const;

export function useQuoteStockAllocation(id: number, enabled: boolean) {
  return useQuery({ queryKey: allocKey(id), queryFn: () => api.get<QuoteStockAllocation>(`/quotes/${id}/stock-allocation`), enabled });
}

export function useReallocateQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post<QuoteStockAllocation>(`/quotes/${id}/stock-allocation`, {}),
    onSuccess: (d) => qc.setQueryData(allocKey(d.quoteId), d),
  });
}
